/* eslint-disable max-lines */
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useSearchParamsState from "../../hooks/useSearchParamsState";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import {useCurrentAccount, useCurrentUser} from "../../App.context";
import {getById, getMapByObjectProperty, isValidEmail, queryByTypenames, separateByTypename} from "../../utils";
import {reverse} from "../../routing";
import query, {
  paginatedListQuery,
  participationsSummaryQuery,
  PickedChallenge,
  PickedProject,
  QueryData,
  ParticipationsSummaryQueryData,
  PaginatedListQueryData,
  PaginatedListQueryVars,
  useAddUsersMutation,
  useInviteParticipantsMutation,
  useSetUsersActivityMutation,
  useUpdateMembershipMutation,
  useUpdateParticipationMutation,
  useArchiveParticipationMutation,
  useImportMembersCsvMutation,
  useSetReportsReceiveMutation,
  usersExportUrlWithFiltersQuery,
  UsersExportUrlWithFiltersQueryData,
  usersExportUrlWithFiltersQueryXls,
  useSetExtraEmailMutation,
  reportsExportUrlsQuery,
  GetAllAccountMembersQueryData,
  getAllAccountMembersQuery,
} from "./members.graphql";
import {
  QueryGetParticipationsSummaryArgs,
  EducationMemberFilterInput,
  EducationMemberRow,
  LanguageCode,
  User,
  EducationMemberSortInput,
  EducationMemberSortField,
  SortDirection,
  UserEmotion,
  ParticipationState,
  ChallengeParticipantsType,
  Participation,
} from "../../schema";
import {TABLE_ROWS_PER_PAGE} from "../../settings";

import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import Breadcrumbs from "../../ui/breadcrumbs";
import MembersFilterForm from "../../components/account/MembersFilterForm";
import MembersTable from "../../components/account/members-table/MembersTable";
import Paginator from "../../ui/paginator";
import AssignModal from "../../components/account/AssignModal";
import {useModal} from "../../components/ModalProvider";
import Button from "../../ui/button";
import DropdownList from "../../ui/dropdownlist";
import {CaretDown} from "../../ui/icons";
import AddMembersModal from "../../components/account/AddMembersModal";
import MembersActivityModal from "../../components/account/MembersActivityModal";
import AccountInfo from "../../components/account/AccountInfo";
import ParticipationActivityModal from "../../components/account/ParticipationsActivityModal";
import ParticipationsSummary from "../../components/account/ParticipationsSummary";
import EducationReports from "../../components/account/EducationReports";
import ReactLink from "../../components/ReactLink";

import classes from "./members.module.css";
import {analyticsSendEvent} from "../../libs/analytics";
import DeleteModal from "../../components/editor/DeleteModal";
import {getLanguageCode} from "../../i18n";
import ImportMembersModal from "../../components/account/ImportMembersModal";
import Textarea from "../../ui/textarea";
import {useFormState} from "../../hooks/useFormState";
import Link from "../../ui/link";
import Checkbox from "../../ui/checkbox";
import Input from "../../ui/input";
import {useDownloadWithToast} from "../../hooks/useDownload";
import {getParticipationReportExportUrl, getUserBestAttemptsReportExportUrl} from "../../utils/player/utils";

const SEARCH_PARAMS_CONFIG: {
  challengeId: {type: "string"};
  projectId: {type: "string"};
  groupsIds: {type: "string"};
  email: {type: "string"};
  username: {type: "string"};
  active: {type: "boolean"};
  completed: {type: "boolean"};
  daysAmount: {type: "integer"};
  adminFilter: {type: "boolean"};
  emotion: {type: "string"};
  participationState: {type: "string"};
} = {
  challengeId: {type: "string"},
  projectId: {type: "string"},
  groupsIds: {type: "string"},
  email: {type: "string"},
  username: {type: "string"},
  active: {type: "boolean"},
  completed: {type: "boolean"},
  daysAmount: {type: "integer"},
  adminFilter: {type: "boolean"},
  emotion: {type: "string"},
  participationState: {type: "string"},
};

export type InviteParticipants = (
  challenges?: ChallengeParticipantsType[],
  usersIds?: User["id"][],
  emails?: string[],
  adminText?: string,
) => void;
export type AddMembers = (emails: string[], names: string[], languageCode?: LanguageCode) => void;
export type SetUsersActivity = (emails: string[], isActive: boolean) => void;

type formState = {
  email: string;
};

export default function AccountMembersScene() {
  const {t, i18n} = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [rawFilter, setFilter] = useSearchParamsState(SEARCH_PARAMS_CONFIG);
  const lang = i18n.language as "en" | "ru";
  const {download} = useDownloadWithToast();
  const currentUser = useCurrentUser();
  const isDeveloper = !!currentUser?.isDeveloper;

  const filter = useMemo(
    () => ({
      ...rawFilter,
      groupsIds: rawFilter.groupsIds !== "" ? rawFilter.groupsIds.split(" ") : undefined,
      emotion: rawFilter.emotion !== "" ? (rawFilter.emotion as UserEmotion) : undefined,
      participationState:
        rawFilter.participationState !== "" ? (rawFilter.participationState as ParticipationState) : undefined,
      daysAmount: rawFilter.daysAmount ?? 0,
    }),
    [rawFilter],
  );

  const defaultSort = useMemo(
    () => ({
      field: EducationMemberSortField.EMAIL,
      direction: SortDirection.ASC,
    }),
    [],
  );

  const [sort, setSort] = useState<EducationMemberSortInput>(defaultSort);

  const setMemebrsFilter = useCallback(
    (data: EducationMemberFilterInput) => {
      const serializedData = {
        ...data,
        challengeId: data.challengeId ?? "",
        projectId: data.projectId ?? "",
        email: data.email ?? "",
        username: data.username ?? "",
        groupsIds: data.groupsIds ? data.groupsIds.join(" ") : "",
        daysAmount: data.daysAmount ?? 0,
        adminFilter: data.adminFilter ?? "",
        emotion: data.emotion ?? "",
        participationState: data.participationState ?? "",
      } as Parameters<typeof setFilter>[0];

      setFilter(serializedData);
    },
    [setFilter],
  );

  const filterChallengeId = filter.challengeId ?? undefined;
  const filterProjectId = filter.projectId ?? undefined;
  const [members, setMembers] = useState<EducationMemberRow[]>([]);

  const {data, loading: fetching} = useQuery<QueryData>(query, {
    variables: {
      lang: i18n.language,
    },
  });

  const {educationItems: items, contracts, membersSummary, account, groups, sessionsExportUrl} = data ?? {};

  const {data: listData, loading: loadingList} = useQuery<PaginatedListQueryData, PaginatedListQueryVars>(
    paginatedListQuery,
    {
      variables: {
        offset: (currentPage - 1) * TABLE_ROWS_PER_PAGE,
        limit: TABLE_ROWS_PER_PAGE,
        filter,
        sort,
      },
      fetchPolicy: "network-only",
    },
  );

  const {data: urlData, loading: loadingUrl} = useQuery<UsersExportUrlWithFiltersQueryData>(
    usersExportUrlWithFiltersQuery,
    {
      variables: {
        filter,
      },
    },
  );

  const {data: urlDataXls} = useQuery<UsersExportUrlWithFiltersQueryData>(usersExportUrlWithFiltersQueryXls, {
    variables: {
      filter,
    },
  });

  const {data: reportUrlsData} = useQuery<{
    userBestAttemptsExportUrlTemplate: string;
    participationBestAttemptReportUrlTemplate: string;
    challengeTranscriptsExportUrl: string;
    challengeTranscriptsModifiedContentExportUrl: string;
    challengeTranscriptsExportUrlXls: string;
    challengeTranscriptsModifiedContentExportUrlXls: string;
  }>(reportsExportUrlsQuery, {
    variables: {
      challengeId: filter.challengeId,
      lang,
    },
  });

  const paginatedList = listData?.paginatedList;
  const usersExportUrlWithFilters = urlData?.usersExportUrlWithFilters;
  const usersExportUrlWithFiltersXls = urlDataXls?.usersExportUrlWithFilters;

  const {data: participationsSummaryData, loading: loadingParticipationsSummary} = useQuery<
    ParticipationsSummaryQueryData,
    QueryGetParticipationsSummaryArgs
  >(participationsSummaryQuery, {
    variables: {
      challengeId: filter.challengeId,
      projectId: filter.projectId,
      groupsIds: filter.groupsIds ?? [],
    },
  });

  const participationsSummary = participationsSummaryData?.participationsSummary;

  const {data: usersData, loading: loadingAllUsers} =
    useQuery<GetAllAccountMembersQueryData>(getAllAccountMembersQuery);

  const users = usersData?.users ?? undefined;

  const flatChallenges = useMemo(
    () =>
      data?.flatChallenges
        ? (separateByTypename(data.flatChallenges).get("Challenge") as PickedChallenge[])
        : undefined,
    [data?.flatChallenges],
  );

  const [challenges, projects] = useMemo(() => {
    if (!items) {
      return [undefined, undefined];
    }
    return queryByTypenames(items, ["Challenge", "Project"]) as [PickedChallenge[], PickedProject[]];
  }, [items]);

  const currentItem = useMemo(() => {
    if (flatChallenges === undefined || projects === undefined) {
      return;
    }
    if (filter.challengeId) {
      return getById(flatChallenges, filter.challengeId);
    }
    if (filter.projectId) {
      return getById(projects, filter.projectId);
    }
  }, [projects, filter.challengeId, filter.projectId, flatChallenges]);

  const mappedGroupsById = useMemo(() => (groups ? getMapByObjectProperty(groups, "id") : undefined), [groups]);

  const currentGroups = useMemo(() => {
    if (mappedGroupsById === undefined || filter.groupsIds === undefined) {
      return;
    }
    return filter.groupsIds.map((id) => mappedGroupsById.get(id)!);
  }, [mappedGroupsById, filter.groupsIds]);

  const loading = !data && fetching;

  const [applyUpdateMembershipMutation] = useUpdateMembershipMutation();
  const [applyUpdateParticipationMutation] = useUpdateParticipationMutation();
  const [applyArchiveParticipationMutation] = useArchiveParticipationMutation();
  const [applySetReportsReceiveMutation] = useSetReportsReceiveMutation();
  const [applySetExtraEmailMutation] = useSetExtraEmailMutation();

  const onSetUserStatus = useCallback(
    (userId: string, isActive: boolean) => {
      applyUpdateMembershipMutation({
        variables: {
          userId,
          data: {
            isActive,
          },
        },
      });
    },
    [applyUpdateMembershipMutation],
  );

  const onSetParticipationStatus = useCallback(
    (
      userId: string | undefined,
      status: boolean,
      challengeId?: string,
      projectId?: string,
      deactivateAt?: number,
      emails?: string[],
    ) => {
      applyUpdateParticipationMutation({
        variables: {
          userId,
          challengeId,
          projectId,
          status,
          emails,
          deactivateAt,
        },
      });
    },
    [applyUpdateParticipationMutation],
  );

  const onParticipationReportExport = useMemo(() => {
    if (!reportUrlsData?.participationBestAttemptReportUrlTemplate) {
      return undefined;
    }

    return (participation: Pick<Participation, "id" | "challenge">, user: Pick<User, "fullName" | "email">) => {
      const downloadUrl = getParticipationReportExportUrl(
        participation.id,
        "pdf",
        reportUrlsData.participationBestAttemptReportUrlTemplate,
      );

      const sanitize = (s: string) => s.replace(/\s+/g, "-").replace(/[^a-zA-Zа-яА-Я0-9_-]/g, "-");
      const userFileName = `${sanitize(user.fullName)}${user.email ? `_${sanitize(user.email)}` : ""}`;
      const fileName =
        `${sanitize(participation.challenge.name || "challenge")}` +
        `${userFileName ? `_${userFileName}` : ""}_report.pdf`;

      download(`${downloadUrl}?lang=${lang}`, fileName);
    };
  }, [reportUrlsData?.participationBestAttemptReportUrlTemplate, download, lang]);

  const onUserBestAttemptsReportExport = useMemo(() => {
    if (!reportUrlsData?.userBestAttemptsExportUrlTemplate) {
      return undefined;
    }

    return (user: Pick<User, "id" | "fullName" | "email">) => {
      const downloadUrl = getUserBestAttemptsReportExportUrl(
        user.id,
        "pdf",
        reportUrlsData.userBestAttemptsExportUrlTemplate,
      );

      const sanitize = (s: string) => s.replace(/\s+/g, "-").replace(/[^a-zA-Zа-яА-Я0-9_-]/g, "-");
      const userFileName = `${sanitize(user.fullName)}${user.email ? `_${sanitize(user.email)}` : ""}`;
      const fileName = `${userFileName ? `_${userFileName}` : ""}_report.pdf`;

      download(`${downloadUrl}?lang=${lang}`, fileName);
    };
  }, [reportUrlsData?.userBestAttemptsExportUrlTemplate, download, lang]);

  const onParticipationDelete = useCallback(
    (userId: string | undefined, challengeId?: string, projectId?: string, emails?: string[]) => {
      applyArchiveParticipationMutation({
        variables: {
          userId,
          challengeId,
          projectId,
          emails,
        },
        refetchQueries: ["GetAccountMembersQuery"],
      });
    },
    [applyArchiveParticipationMutation],
  );

  const [applyAddMembersMutation] = useAddUsersMutation();
  const [applySetUsersActivityMutation] = useSetUsersActivityMutation();
  const [applyInviteParticipantsMutation] = useInviteParticipantsMutation();
  const [applyImportMembersCsvMutation] = useImportMembersCsvMutation();

  const onAddMembers: AddMembers = useCallback(
    (emails, names, languageCode) => {
      applyAddMembersMutation({
        variables: {
          emails,
          names,
          languageCode,
        },
      });
    },
    [applyAddMembersMutation],
  );

  const onSetActivity: SetUsersActivity = useCallback(
    (emails, isActive) => {
      applySetUsersActivityMutation({
        variables: {
          emails,
          isActive,
        },
      });
    },
    [applySetUsersActivityMutation],
  );

  const onAssign: InviteParticipants = useCallback(
    (challenges, usersIds, emails, adminText) => {
      applyInviteParticipantsMutation({
        variables: {
          challenges: challenges?.map((challenge) => ({
            challengeId: challenge.challengeId,
            activateAt: challenge.activateAt,
            deactivateAt: challenge.deactivateAt,
          })),
          usersIds,
          emails,
          adminText,
        },
        refetchQueries: ["GetAccountMembersQuery"],
      });
    },
    [applyInviteParticipantsMutation],
  );

  const {add: addModal} = useModal();
  const onAddMembersButtonClick = useCallback(() => {
    const addMembers = (emails: string[], names: string[], languageCode?: LanguageCode) => {
      onAddMembers(emails, names, languageCode);
      modal.remove();
    };

    const modal = addModal({
      header: <AddMembersModal.Header />,
      content: <AddMembersModal.Content onAddMembers={addMembers} />,
    });
  }, [addModal, onAddMembers]);

  const onParticipationDeleteButtonClick = useCallback(
    (userId: string | undefined, challengeId?: string, projectId?: string, emails?: string[]) => {
      const onDelete = () => {
        analyticsSendEvent("membersParticipationDelete", {
          userId: userId,
          challengeId: challengeId,
        });
        onParticipationDelete(userId, challengeId, projectId, emails);
        modal.remove();
      };

      const onCancel = () => modal.remove();

      const modal = addModal({
        id: `deleteParticipation_${challengeId}`,
        header: t("members.deleteParticipation"),
        content: t("members.deleteParticipationConfirmation"),
        footer: <DeleteModal.Footer onConfirm={onDelete} onCancel={onCancel} />,
      });
    },
    [t, onParticipationDelete, addModal],
  );

  const onMembersActivityButtonClick = useCallback(() => {
    const setActivity = (emails: string[], isActive: boolean) => {
      onSetActivity(emails, isActive);
      modal.remove();
    };

    const modal = addModal({
      header: <MembersActivityModal.Header />,
      content: <MembersActivityModal.Content onSetUsersActivity={setActivity} />,
    });
  }, [addModal, onSetActivity]);

  const onImportMembers = useCallback(
    (attachment: File, sendWelcomeEmails: boolean, overrideActive: boolean) => {
      applyImportMembersCsvMutation({
        variables: {
          attachment: attachment,
          sendWelcomeEmails: sendWelcomeEmails,
          overrideActive: overrideActive,
          emailLanguage: getLanguageCode(i18n.language),
        },
        refetchQueries: ["GetAccountMembersQuery"],
      }).then(({data}) => {
        addModal(
          {
            id: "ImportCSVResult",
            header: t("components.members.ImportMembersModal.result"),
            content: <Textarea className={classes.input} name="result" rows={6} readOnly value={data?.result} />,
          },
          true,
        );
      });
    },
    [applyImportMembersCsvMutation, addModal, t, i18n.language],
  );

  const onImportMembersButtonClick = useCallback(() => {
    const importMembers = (attachment: File, sendWelcomeEmails: boolean, overrideActive: boolean) => {
      onImportMembers(attachment, sendWelcomeEmails, overrideActive);
      modal.remove();
    };

    const modal = addModal({
      header: <ImportMembersModal.Header />,
      content: <ImportMembersModal.Content onImportMembers={importMembers} />,
    });
  }, [addModal, onImportMembers]);

  const onParticipationsActivityButtonClick = useCallback(() => {
    const setActivity = (
      emails: string[],
      isActive: boolean,
      challengeId?: string,
      projectId?: string,
      deactivateAt?: number,
    ) => {
      onSetParticipationStatus(undefined, isActive, challengeId, projectId, deactivateAt, emails);
      modal.remove();
    };
    const deleteParticipation = (emails: string[], challengeId?: string, projectId?: string) => {
      onParticipationDeleteButtonClick(undefined, challengeId, projectId, emails);
      modal.remove();
    };

    const modal = addModal({
      header: <ParticipationActivityModal.Header />,
      content: (
        <ParticipationActivityModal.Content
          challenges={flatChallenges}
          projects={projects}
          params={{
            onAssign: setActivity,
            onDelete: deleteParticipation,
          }}
        />
      ),
    });
  }, [addModal, flatChallenges, projects, onSetParticipationStatus, onParticipationDeleteButtonClick]);

  const onAssignButtonClick = useMemo(() => {
    if (loadingAllUsers) {
      return;
    }

    return () => {
      if (!users || !groups) {
        return;
      }
      const assignChallengeToUser: InviteParticipants = (challenges, usersIds, emails, adminText) => {
        onAssign(challenges, usersIds, emails, adminText);
        modal.remove();
      };

      const id = (filterChallengeId ?? filterProjectId)!;
      const type = filterChallengeId ? "Challenge" : filterProjectId ? "Project" : undefined;

      const modal = addModal({
        size: "l",
        header: <AssignModal.Header />,
        content: (
          <AssignModal.Content
            initialId={id}
            initialType={type}
            challenges={challenges}
            projects={projects}
            params={{
              multipleUsers: true,
              onAssign: assignChallengeToUser,
              users: users,
              groups: groups,
            }}
          />
        ),
      });
    };
  }, [users, groups, filterChallengeId, filterProjectId, addModal, challenges, projects, onAssign, loadingAllUsers]);

  useEffect(() => {
    if (!loading) {
      setMembers(paginatedList?.items ?? []);
    }
  }, [loading, paginatedList]);

  const itemsCount = Math.min(members.length, TABLE_ROWS_PER_PAGE);
  const totalItems = paginatedList?.countItems ?? 0;

  const totalPages = Math.ceil(totalItems / TABLE_ROWS_PER_PAGE);

  const dropdownManageMenu = useMemo(
    () => (
      <>
        <DropdownList.Action title={t("members.addUsers")} onClick={onAddMembersButtonClick} />
        <DropdownList.Action title={t("members.membersActivity")} onClick={onMembersActivityButtonClick} />
        <DropdownList.Action
          title={t("members.participationsActivity")}
          onClick={onParticipationsActivityButtonClick}
        />
      </>
    ),
    [t, onAddMembersButtonClick, onMembersActivityButtonClick, onParticipationsActivityButtonClick],
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages !== 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const reports = useMemo(
    () => [
      {
        url: sessionsExportUrl || "",
        filename: "sessions.csv",
        title: t("members.reports.sessions"),
      },
      ...(isDeveloper && !!filter.challengeId
        ? [
            {
              url: reportUrlsData?.challengeTranscriptsExportUrl || "",
              filename: "challengeTranscripts.csv",
              title: t("members.reports.challengeTranscripts"),
            },
            {
              url: reportUrlsData?.challengeTranscriptsExportUrlXls || "",
              filename: "challengeTranscriptsXls.csv",
              title: t("members.reports.challengeTranscriptsXls"),
            },
            {
              url: reportUrlsData?.challengeTranscriptsModifiedContentExportUrl || "",
              filename: "modifiedChallengeTranscripts.csv",
              title: t("members.reports.challengeTranscriptsModifiedContent"),
            },
            {
              url: reportUrlsData?.challengeTranscriptsModifiedContentExportUrlXls || "",
              filename: "modifiedChallengeTranscriptsXls.csv",
              title: t("members.reports.challengeTranscriptsModifiedContentXls"),
            },
          ]
        : []),
    ],
    [
      t,
      sessionsExportUrl,
      reportUrlsData?.challengeTranscriptsExportUrl,
      reportUrlsData?.challengeTranscriptsModifiedContentExportUrl,
      reportUrlsData?.challengeTranscriptsExportUrlXls,
      reportUrlsData?.challengeTranscriptsModifiedContentExportUrlXls,
      isDeveloper,
      filter.challengeId,
    ],
  );

  const [receiveReports, setReceiveReports] = useState(useCurrentAccount()?.receiveReports ?? false);

  const toggleReceiveReports = useCallback(() => {
    setReceiveReports(!receiveReports);

    applySetReportsReceiveMutation({
      variables: {
        status: !receiveReports,
      },
      refetchQueries: ["AppQuery"],
    });
  }, [receiveReports, applySetReportsReceiveMutation]);

  const formState = useFormState<formState>({
    initialValues: {
      email: useCurrentUser()?.extraEmail ?? "",
    },
    preventDefault: true,
    onChange: () => {
      var validEmail: string | undefined = undefined;
      if (isValidEmail(formState.values.email)) {
        validEmail = formState.values.email;
      }

      if (validEmail) {
        applySetExtraEmailMutation({
          variables: {
            email: validEmail,
          },
          refetchQueries: ["AppQuery"],
        });
      }
    },
  });

  return (
    <>
      <ReactDocumentTitle title={t("members.title")} />

      <Layout>
        <Header />
        <Main>
          <Container className={classes.main}>
            <Breadcrumbs>
              <span>{t("breadcrumbs.members")}</span>
            </Breadcrumbs>
            <div className={classes.header}>
              <h1 className={classes.h1}>{t("members.h1")}</h1>
              <div className={classes.topRightMenu}>
                <Button className={classes.menuButton} onClick={onImportMembersButtonClick}>
                  {t("components.ImportMembersModal.button")}
                </Button>
                <Button onClick={onAssignButtonClick} className={classes.first} disabled={!onAssignButtonClick}>
                  {t("members.assign")}
                </Button>
                <DropdownList
                  rounded
                  dropShadow
                  placement="bottom-end"
                  offset={[0, 8]}
                  className={classes.secondA}
                  content={dropdownManageMenu}
                >
                  <Button className={classes.menuButton}>
                    {t("members.manage")} <CaretDown />
                  </Button>
                </DropdownList>
                <EducationReports
                  reports={reports}
                  buttonClassName={classes.menuButton}
                  dropdownClassName={classes.secondB}
                />
                <Button className={classes.third} as={ReactLink} href={reverse("accountMembersGroups")}>
                  {t("members.groups")}
                </Button>
                <Button as={ReactLink} href={reverse("anonymousSessions")}>
                  {t("anonymousSessions.sessions")}
                </Button>
              </div>
            </div>

            {!loading && <AccountInfo contracts={contracts!} membersSummary={membersSummary!} account={account!} />}

            <div className={classes.content}>
              <div className={classes.contentMain}>
                {!loadingParticipationsSummary && participationsSummary && (
                  <ParticipationsSummary
                    className={classes.summary}
                    currentItem={currentItem}
                    currentGroups={currentGroups}
                    participationsSummary={participationsSummary}
                  />
                )}
                <div className={classes.tableRoot}>
                  {!loading && !loadingList ? (
                    <MembersTable
                      members={members}
                      challenges={challenges}
                      projects={projects}
                      onSetStatus={onSetUserStatus}
                      onSetParticipationStatus={onSetParticipationStatus}
                      onAssign={onAssign}
                      onParticipationDelete={onParticipationDeleteButtonClick}
                      onUserBestAttemptsReportExport={onUserBestAttemptsReportExport}
                      onParticipationReportExport={onParticipationReportExport}
                      unambiguousSubRow={!!filter.challengeId}
                      onSetSort={setSort}
                      sort={sort}
                      defaultSort={defaultSort}
                    />
                  ) : (
                    <MembersTable.Skeleton />
                  )}
                </div>

                <div className={classes.paginatorRow}>
                  <div className={classes.paginatorHelpText}>
                    {t("common.paginatorShowingItems", {
                      items: itemsCount,
                      totalItems,
                    })}
                  </div>

                  {totalPages > 1 && (
                    <Paginator
                      className={classes.paginator}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  )}
                </div>
              </div>
              <div className={classes.contentAside}>
                <MembersFilterForm
                  initialValues={filter}
                  onSave={setMemebrsFilter}
                  challenges={challenges}
                  projects={projects}
                  groups={groups}
                />
                {!loadingUrl && usersExportUrlWithFilters && (
                  <div className={classes.downloadLink}>
                    <span className="mr-1">{t("members.reports.usersWithFilters")}</span>
                    <Link as="a" href={usersExportUrlWithFilters}>
                      {"CSV"}
                    </Link>
                    <span>{" / "}</span>
                    <Link as="a" href={usersExportUrlWithFiltersXls}>
                      {"XLS"}
                    </Link>
                  </div>
                )}
                <div className={classes.formItem}>
                  <Checkbox checked={receiveReports} onChange={toggleReceiveReports} />
                  <span className={classes.checkboxText}>{t("members.receiveReports")}</span>
                </div>
                <span className={classes.formSubItem}>{t("members.extraEmail")}</span>
                <Input
                  name="email"
                  displaySize="sm"
                  placeholder="example@gmail.com"
                  value={formState.values.email ?? undefined}
                  onChange={formState.changeHandler}
                />
              </div>
            </div>
          </Container>
        </Main>
      </Layout>
    </>
  );
}
